// GUI 主入口：参数面板 ↔ 处理管线
import { hexToRgb } from "./color";
import { computeLayout, type Box } from "./draw";
import { clamp01 } from "./geometry";
import {
  collectImages,
  downloadBlob,
  ensureSubDir,
  isFileSystemAccessSupported,
  pickDirectory,
  writeFile,
  writeInPlace,
  type DirHandleLike,
  type SourceItem,
} from "./fs";
import { createRunner } from "./pool";
import { processImage } from "./process";
import type { LogoPayload } from "./protocol";
import {
  DEFAULT_OPTIONS,
  DEFAULT_SETTINGS,
  IMAGE_EXTENSIONS,
  type Anchor,
  type PositionMode,
  type ProcessOptions,
  type RGBA,
  type Settings,
} from "./types";

const STORAGE_KEY = "watermark-adder-settings-v1";

function el<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`缺少元素 #${id}`);
  return node as T;
}

const panel = el<HTMLElement>("panel");
const sourceInfo = el<HTMLParagraphElement>("sourceInfo");
const logoInfo = el<HTMLParagraphElement>("logoInfo");
const previewInfo = el<HTMLParagraphElement>("previewInfo");
const previewCanvas = el<HTMLCanvasElement>("preview");
const previewSelect = el<HTMLSelectElement>("previewSelect");
const logEl = el<HTMLPreElement>("log");
const barEl = el<HTMLElement>("bar");
const progressText = el<HTMLSpanElement>("progressText");
const okEl = el<HTMLElement>("okCount");
const failEl = el<HTMLElement>("failCount");
const startBtn = el<HTMLButtonElement>("start");

type SourceMode = "dir" | "files" | "demo";
type OutMode = "subdir" | "inplace" | "download";

const state: {
  mode: SourceMode;
  dir: DirHandleLike | null;
  items: SourceItem[];
  logo: { payload: LogoPayload; bitmap: ImageBitmap; name: string } | null;
  running: boolean;
  previewIndex: number;
  previewToken: number;
  /** 最近一次预览的成品图，拖拽时用来快速重绘 */
  previewBitmap: ImageBitmap | null;
  ok: number;
  fail: number;
} = {
  mode: "files",
  dir: null,
  items: [],
  logo: null,
  running: false,
  previewIndex: 0,
  previewToken: 0,
  previewBitmap: null,
  ok: 0,
  fail: 0,
};

/** 定位方式：点/拖自由坐标就切到 free，动九宫格参数就切回 anchor */
let positionMode: PositionMode = DEFAULT_SETTINGS.positionMode;

// ---------- 参数读写 ----------

function num(id: string, fallback: number): number {
  const v = Number.parseFloat(el<HTMLInputElement>(id).value);
  return Number.isFinite(v) ? v : fallback;
}

const clamp255 = (v: number) => Math.max(0, Math.min(255, Math.round(v)));

function colorOf(colorId: string, alphaId: string): RGBA {
  const [r, g, b] = hexToRgb(el<HTMLInputElement>(colorId).value);
  return [r, g, b, clamp255(num(alphaId, 255))];
}

function readSettings(): Settings {
  return {
    text: el<HTMLInputElement>("text").value,
    fontFamily: el<HTMLInputElement>("font").value.trim() || DEFAULT_SETTINGS.fontFamily,
    fontSizeRatio: num("fontSize", DEFAULT_SETTINGS.fontSizeRatio),
    textColor: colorOf("textColor", "textAlpha"),
    strokeColor: colorOf("strokeColor", "strokeAlpha"),
    strokeWidthRatio: num("strokeWidth", DEFAULT_SETTINGS.strokeWidthRatio),
    boxColor: colorOf("boxColor", "boxAlpha"),
    boxPaddingRatio: num("boxPadding", DEFAULT_SETTINGS.boxPaddingRatio),
    boxRadiusRatio: num("boxRadius", DEFAULT_SETTINGS.boxRadiusRatio),
    anchor: el<HTMLSelectElement>("anchor").value as Anchor,
    offsetX: num("offX", DEFAULT_SETTINGS.offsetX),
    offsetY: num("offY", DEFAULT_SETTINGS.offsetY),
    positionMode,
    freeX: clamp01(num("freeX", DEFAULT_SETTINGS.freeX * 100) / 100),
    freeY: clamp01(num("freeY", DEFAULT_SETTINGS.freeY * 100) / 100),
    maskEnabled: el<HTMLInputElement>("maskEnabled").checked,
    maskRect: [num("maskX0", 0), num("maskY0", 0), num("maskX1", 0), num("maskY1", 0)],
    maskColor: colorOf("maskColor", "maskAlpha"),
    logoScaleRatio: num("logoScale", DEFAULT_SETTINGS.logoScaleRatio),
    logoOpacity: clamp255(num("logoOpacity", DEFAULT_SETTINGS.logoOpacity)),
    tileEnabled: el<HTMLInputElement>("tileEnabled").checked,
    tileAngle: num("tileAngle", DEFAULT_SETTINGS.tileAngle),
    tileGapRatio: num("tileGap", DEFAULT_SETTINGS.tileGapRatio),
  };
}

function readOptions(): ProcessOptions {
  return {
    jpegQuality: Math.max(0.1, Math.min(1, num("quality", DEFAULT_OPTIONS.jpegQuality))),
    copyright: el<HTMLInputElement>("copyright").value.trim(),
  };
}

interface UiExtras {
  outMode: OutMode;
  outDirName: string;
  concurrency: number;
}

function readExtras(): UiExtras {
  return {
    outMode: el<HTMLSelectElement>("outMode").value as OutMode,
    outDirName: el<HTMLInputElement>("outDirName").value.trim() || "out",
    concurrency: Math.max(1, Math.min(8, Math.round(num("concurrency", 4)))),
  };
}

/** 把设置写回表单（读取本地存储时用） */
function applySettings(st: Settings, opts: ProcessOptions, extras: UiExtras): void {
  el<HTMLInputElement>("text").value = st.text;
  el<HTMLInputElement>("font").value = st.fontFamily;
  el<HTMLInputElement>("fontSize").value = String(st.fontSizeRatio);
  el<HTMLInputElement>("strokeWidth").value = String(st.strokeWidthRatio);
  el<HTMLSelectElement>("anchor").value = st.anchor;
  el<HTMLInputElement>("offX").value = String(st.offsetX);
  el<HTMLInputElement>("offY").value = String(st.offsetY);
  positionMode = st.positionMode;
  el<HTMLInputElement>("freeX").value = (st.freeX * 100).toFixed(1);
  el<HTMLInputElement>("freeY").value = (st.freeY * 100).toFixed(1);
  el<HTMLInputElement>("boxPadding").value = String(st.boxPaddingRatio);
  el<HTMLInputElement>("boxRadius").value = String(st.boxRadiusRatio);
  el<HTMLInputElement>("maskEnabled").checked = st.maskEnabled;
  el<HTMLInputElement>("maskX0").value = String(st.maskRect[0]);
  el<HTMLInputElement>("maskY0").value = String(st.maskRect[1]);
  el<HTMLInputElement>("maskX1").value = String(st.maskRect[2]);
  el<HTMLInputElement>("maskY1").value = String(st.maskRect[3]);
  el<HTMLInputElement>("logoScale").value = String(st.logoScaleRatio);
  el<HTMLInputElement>("tileEnabled").checked = st.tileEnabled;
  el<HTMLInputElement>("tileAngle").value = String(st.tileAngle);
  el<HTMLInputElement>("tileGap").value = String(st.tileGapRatio);
  el<HTMLInputElement>("quality").value = String(opts.jpegQuality);
  el<HTMLInputElement>("copyright").value = opts.copyright;
  el<HTMLSelectElement>("outMode").value = extras.outMode;
  el<HTMLInputElement>("outDirName").value = extras.outDirName;
  el<HTMLInputElement>("concurrency").value = String(extras.concurrency);

  const setColor = (colorId: string, alphaId: string, c: RGBA) => {
    el<HTMLInputElement>(colorId).value = `#${[c[0], c[1], c[2]].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
    el<HTMLInputElement>(alphaId).value = String(c[3]);
  };
  setColor("textColor", "textAlpha", st.textColor);
  setColor("strokeColor", "strokeAlpha", st.strokeColor);
  setColor("boxColor", "boxAlpha", st.boxColor);
  setColor("maskColor", "maskAlpha", st.maskColor);
  el<HTMLInputElement>("logoOpacity").value = String(st.logoOpacity);
  syncOutputs();
  updatePosHint();
}

/** 切换定位方式（九宫格 / 自由坐标） */
function setPositionMode(mode: PositionMode): void {
  positionMode = mode;
  updatePosHint();
}

const ANCHOR_LABELS: Record<Anchor, string> = {
  "top-left": "左上角",
  "top-center": "顶部居中",
  "top-right": "右上角",
  "middle-left": "左侧居中",
  center: "中心",
  "middle-right": "右侧居中",
  "bottom-left": "左下角",
  "bottom-center": "底部居中",
  "bottom-right": "右下角",
};

function updatePosHint(): void {
  const hint = el<HTMLParagraphElement>("posHint");
  if (el<HTMLInputElement>("tileEnabled").checked) {
    hint.textContent = "平铺模式下位置参数不生效（水印铺满整图），关掉平铺才能拖动定位。";
    return;
  }
  if (positionMode === "free") {
    hint.textContent = `当前定位：自由坐标（水印左上角位于可移动范围的 X ${el<HTMLInputElement>("freeX").value}%、Y ${el<HTMLInputElement>("freeY").value}%）。在预览图上继续拖动可微调。`;
  } else {
    hint.textContent = `当前定位：九宫格（${ANCHOR_LABELS[el<HTMLSelectElement>("anchor").value as Anchor]}，偏移 ${el<HTMLInputElement>("offX").value} / ${el<HTMLInputElement>("offY").value}）。在预览图上按住水印拖动可自由摆放。`;
  }
}

/** 刷新滑杆旁边的数值显示 */
function syncOutputs(): void {
  for (const [id, out] of [
    ["textAlpha", "textAlphaOut"],
    ["strokeAlpha", "strokeAlphaOut"],
    ["boxAlpha", "boxAlphaOut"],
    ["maskAlpha", "maskAlphaOut"],
    ["logoOpacity", "logoOpacityOut"],
  ] as const) {
    el<HTMLOutputElement>(out).textContent = el<HTMLInputElement>(id).value;
  }
}

function saveState(): void {
  const payload = { settings: readSettings(), options: readOptions(), extras: readExtras() };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
}

function loadState(): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as { settings?: Partial<Settings>; options?: Partial<ProcessOptions>; extras?: Partial<UiExtras> };
    applySettings(
      { ...DEFAULT_SETTINGS, ...parsed.settings },
      { ...DEFAULT_OPTIONS, ...parsed.options },
      { outMode: "subdir", outDirName: "out", concurrency: 4, ...parsed.extras },
    );
  } catch {
    applySettings(DEFAULT_SETTINGS, DEFAULT_OPTIONS, { outMode: "subdir", outDirName: "out", concurrency: 4 });
  }
}

// ---------- 日志与进度 ----------

function log(line: string): void {
  const time = new Date().toLocaleTimeString("zh-CN", { hour12: false });
  logEl.textContent += `[${time}] ${line}\n`;
  const lines = logEl.textContent.split("\n");
  if (lines.length > 400) logEl.textContent = lines.slice(-300).join("\n");
  logEl.scrollTop = logEl.scrollHeight;
}

function setProgress(done: number, total: number): void {
  barEl.style.width = total > 0 ? `${(done / total) * 100}%` : "0%";
  progressText.textContent = `进度 ${done}/${total}`;
}

function refreshCounters(): void {
  okEl.textContent = String(state.ok);
  failEl.textContent = String(state.fail);
}

// ---------- 选图 ----------

function setItems(items: SourceItem[], mode: SourceMode, note: string): void {
  state.items = items;
  state.mode = mode;
  state.previewIndex = 0;
  previewSelect.innerHTML = "";
  items.slice(0, 200).forEach((item, index) => {
    const option = document.createElement("option");
    option.value = String(index);
    option.textContent = item.path;
    previewSelect.appendChild(option);
  });
  sourceInfo.textContent = note;
  progressText.textContent = "就绪";
  setProgress(0, 0);
  schedulePreview(true);
}

async function loadFiles(files: File[]): Promise<void> {
  const items: SourceItem[] = [];
  for (const file of files) {
    const dot = file.name.lastIndexOf(".");
    const ext = dot >= 0 ? file.name.slice(dot).toLowerCase() : "";
    if (!IMAGE_EXTENSIONS.has(ext)) continue;
    items.push({ path: file.name, name: file.name, file });
  }
  setItems(items, "files", items.length ? `已选择 ${items.length} 个文件（输出方式见第 6 步）` : "选中的文件里没有支持的图片格式");
}

async function pickDirectorySource(): Promise<void> {
  if (!isFileSystemAccessSupported()) {
    log("当前浏览器不支持文件夹读写（建议用 Chrome / Edge，并通过 http 打开页面）");
    return;
  }
  try {
    const dir = await pickDirectory();
    if (!dir) return;
    const extras = readExtras();
    const items = await collectImages(dir, IMAGE_EXTENSIONS, new Set([extras.outDirName]));
    state.dir = dir;
    setItems(items, "dir", `文件夹「${dir.name}」中找到 ${items.length} 张图片`);
  } catch (err) {
    log(`选择文件夹失败: ${msg(err)}`);
  }
}

function msg(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/** 生成一张带「假水印」的示例图，方便直接试参数（也用于自动化验证） */
async function makeDemoFile(): Promise<File> {
  const width = 1600;
  const height = 1000;
  const canvas = new OffscreenCanvas(width, height);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("无法创建画布");
  const gradient = ctx.createLinearGradient(0, 0, width, height);
  gradient.addColorStop(0, "#1b3a5c");
  gradient.addColorStop(0.5, "#2f7d8f");
  gradient.addColorStop(1, "#c9a227");
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.font = '40px "Microsoft YaHei", sans-serif';
  ctx.fillText("示例图片 · 试参数用", 60, 100);
  ctx.font = '32px "Microsoft YaHei", sans-serif';
  ctx.fillText("下方灰色文字模拟原图自带的旧水印，可用「遮挡色块」盖掉", 60, 160);
  ctx.font = '46px "Microsoft YaHei", sans-serif';
  ctx.fillStyle = "rgba(20,20,20,0.75)";
  ctx.fillText("原图旧水印 DEMO 2026-09-12", 40, 940);
  const blob = await canvas.convertToBlob({ type: "image/jpeg", quality: 0.92 });
  return new File([blob], "demo.jpg", { type: "image/jpeg" });
}

// ---------- 预览 ----------

let previewTimer: ReturnType<typeof setTimeout> | undefined;

function schedulePreview(immediate = false): void {
  if (previewTimer !== undefined) clearTimeout(previewTimer);
  previewTimer = setTimeout(() => {
    void renderPreview();
  }, immediate ? 0 : 200);
}

async function renderPreview(): Promise<void> {
  const item = state.items[state.previewIndex];
  if (!item) {
    previewInfo.textContent = "预览区：选择图片后自动按当前参数渲染（不写入磁盘）。";
    const ctx = previewCanvas.getContext("2d");
    ctx?.clearRect(0, 0, previewCanvas.width, previewCanvas.height);
    return;
  }
  const token = ++state.previewToken;
  try {
    const buf = await item.file.arrayBuffer();
    const result = await processImage(item.name, buf, readSettings(), readOptions(), state.logo?.bitmap ?? null);
    if (token !== state.previewToken) return;
    const bitmap = await createImageBitmap(result.blob);
    previewCanvas.width = bitmap.width;
    previewCanvas.height = bitmap.height;
    previewCanvas.getContext("2d")?.drawImage(bitmap, 0, 0);
    state.previewBitmap?.close();
    state.previewBitmap = bitmap;
    previewInfo.textContent = `${item.path} · ${result.width}×${result.height} · 输出 ${result.outName} · ${(result.blob.size / 1024).toFixed(0)}KB`;
  } catch (err) {
    if (token === state.previewToken) previewInfo.textContent = `预览失败: ${msg(err)}`;
  }
}

// ---------- 批处理 ----------

async function start(): Promise<void> {
  if (state.running) return;
  if (state.items.length === 0) {
    log("没有可处理的图片，请先选择文件夹或图片文件");
    return;
  }

  const settings = readSettings();
  const options = readOptions();
  const extras = readExtras();

  let outDir: DirHandleLike | null = null;
  let effectiveMode: OutMode = extras.outMode;
  if (effectiveMode === "subdir") {
    if (state.dir) {
      outDir = await ensureSubDir(state.dir, extras.outDirName);
    } else {
      log(`提示：当前是「${state.mode === "dir" ? "文件夹" : "文件"}」来源，无法写入子目录，自动改为逐张下载`);
      effectiveMode = "download";
    }
  }
  if (effectiveMode === "inplace" && state.items.some((i) => !i.handle)) {
    log("提示：部分图片不是来自文件夹，无法原地覆盖，这些图片将改为下载");
  }

  state.running = true;
  state.ok = 0;
  state.fail = 0;
  refreshCounters();
  startBtn.disabled = true;
  const started = performance.now();

  const runner = createRunner(extras.concurrency, settings, options, state.logo?.payload ?? null, (m) => log(m));
  log(`开始处理 ${state.items.length} 张图片（并行度 ${runner.parallel}${runner.fallback ? "，降级模式" : ""}）`);

  let done = 0;
  let id = 0;
  const parallel = runner.parallel;

  try {
    for (let i = 0; i < state.items.length; i += parallel) {
      const chunk = state.items.slice(i, i + parallel);
      await Promise.all(
        chunk.map(async (item) => {
          try {
            const buf = await item.file.arrayBuffer();
            const result = await runner.run({ id: id++, name: item.name, buf });
            await writeOutput(item, result.blob, result.outName, effectiveMode, outDir);
            state.ok++;
            log(`成功 ${item.path} -> ${effectiveMode === "inplace" ? item.name : result.outName}`);
          } catch (err) {
            state.fail++;
            log(`失败 ${item.path}: ${msg(err)}`);
          } finally {
            done++;
            setProgress(done, state.items.length);
            refreshCounters();
          }
        }),
      );
    }
  } finally {
    runner.close();
    state.running = false;
    startBtn.disabled = false;
  }

  const seconds = ((performance.now() - started) / 1000).toFixed(1);
  progressText.textContent = `完成 ${state.ok}/${state.items.length}`;
  log(`处理完成：成功 ${state.ok}，失败 ${state.fail}，耗时 ${seconds}s`);
}

async function writeOutput(
  item: SourceItem,
  blob: Blob,
  outName: string,
  mode: OutMode,
  outDir: DirHandleLike | null,
): Promise<void> {
  if (mode === "inplace" && item.handle) {
    await writeInPlace(item.handle, blob);
    return;
  }
  if (mode === "subdir" && outDir) {
    await writeFile(outDir, outName, blob);
    return;
  }
  downloadBlob(blob, outName);
}

// ---------- 事件绑定 ----------

el<HTMLButtonElement>("pickDir").addEventListener("click", () => void pickDirectorySource());

el<HTMLButtonElement>("pickFiles").addEventListener("click", () => {
  const input = document.createElement("input");
  input.type = "file";
  input.multiple = true;
  input.accept = "image/*";
  input.addEventListener("change", () => {
    void loadFiles(Array.from(input.files ?? []));
  });
  input.click();
});

el<HTMLButtonElement>("demo").addEventListener("click", async () => {
  try {
    const file = await makeDemoFile();
    setItems([{ path: file.name, name: file.name, file }], "demo", "已载入示例图（输出方式为「逐张下载」）");
    log("已载入示例图，可直接点「开始处理」验证流程");
  } catch (err) {
    log(`生成示例图失败: ${msg(err)}`);
  }
});

el<HTMLButtonElement>("clear").addEventListener("click", () => {
  state.dir = null;
  setItems([], "files", "已清空，请重新选择图片。");
  logEl.textContent = "";
  state.ok = 0;
  state.fail = 0;
  refreshCounters();
});

el<HTMLInputElement>("logoFile").addEventListener("change", async (event) => {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  state.logo?.bitmap.close();
  state.logo = null;
  if (!file) {
    logoInfo.textContent = "未选择 LOGO。";
    schedulePreview();
    return;
  }
  try {
    const payload: LogoPayload = { buf: await file.arrayBuffer(), type: file.type || "image/png" };
    state.logo = { payload, bitmap: await createImageBitmap(file), name: file.name };
    logoInfo.textContent = `已选择 ${file.name}`;
  } catch (err) {
    logoInfo.textContent = `LOGO 加载失败: ${msg(err)}`;
  }
  schedulePreview();
});

previewSelect.addEventListener("change", () => {
  state.previewIndex = Number.parseInt(previewSelect.value, 10) || 0;
  void renderPreview();
});

// ---------- 预览里拖动定位 ----------

const drag = {
  active: false,
  box: { x: 0, y: 0, w: 0, h: 0 } as Box,
  grabX: 0,
  grabY: 0,
};

function logoSizeOf(): { width: number; height: number } | null {
  return state.logo?.bitmap ?? null;
}

/** 屏幕坐标 → 图片像素坐标（画布可能被 CSS 缩放） */
function toImageCoords(event: PointerEvent): { x: number; y: number } {
  const rect = previewCanvas.getBoundingClientRect();
  return {
    x: ((event.clientX - rect.left) * previewCanvas.width) / Math.max(1, rect.width),
    y: ((event.clientY - rect.top) * previewCanvas.height) / Math.max(1, rect.height),
  };
}

/** 用当前参数算一遍水印块的落点（与预览里渲染的一致） */
function currentLayout(): Box | null {
  const ctx = previewCanvas.getContext("2d");
  if (!ctx || previewCanvas.width === 0) return null;
  const layout = computeLayout(ctx, previewCanvas.width, previewCanvas.height, readSettings(), logoSizeOf());
  return layout ? layout.box : null;
}

/** 拖动时只重绘「成品图 + 虚线框」，避免每帧跑完整管线 */
function drawDragOverlay(box: Box): void {
  const bitmap = state.previewBitmap;
  const ctx = previewCanvas.getContext("2d");
  if (!bitmap || !ctx) return;
  ctx.clearRect(0, 0, previewCanvas.width, previewCanvas.height);
  ctx.drawImage(bitmap, 0, 0);
  ctx.save();
  ctx.strokeStyle = "#3b82f6";
  ctx.lineWidth = Math.max(2, previewCanvas.width / 400);
  ctx.setLineDash([12, 8]);
  ctx.strokeRect(box.x, box.y, box.w, box.h);
  ctx.restore();
}

function isPointInside(point: { x: number; y: number }, box: Box): boolean {
  const tolerance = Math.max(8, previewCanvas.width * 0.012);
  return (
    point.x >= box.x - tolerance &&
    point.x <= box.x + box.w + tolerance &&
    point.y >= box.y - tolerance &&
    point.y <= box.y + box.h + tolerance
  );
}

previewCanvas.addEventListener("pointerdown", (event) => {
  if (el<HTMLInputElement>("tileEnabled").checked || state.items.length === 0) return;
  const box = currentLayout();
  if (!box) return;
  const point = toImageCoords(event);
  if (!isPointInside(point, box)) return;

  event.preventDefault();
  drag.active = true;
  drag.box = box;
  drag.grabX = point.x - box.x; // 记录抓取点相对水印块的位置，拖动时手感一致
  drag.grabY = point.y - box.y;
  try {
    previewCanvas.setPointerCapture(event.pointerId);
  } catch {
    // 极少数情况下（合成事件 / 指针已释放）拿不到捕获，不影响拖动逻辑
  }
  previewCanvas.style.cursor = "grabbing";
});

previewCanvas.addEventListener("pointermove", (event) => {
  if (!drag.active) {
    if (el<HTMLInputElement>("tileEnabled").checked || state.items.length === 0) {
      previewCanvas.style.cursor = "default";
      return;
    }
    const box = currentLayout();
    const point = toImageCoords(event);
    previewCanvas.style.cursor = box && isPointInside(point, box) ? "move" : "default";
    return;
  }

  const point = toImageCoords(event);
  const maxX = Math.max(1, previewCanvas.width - drag.box.w);
  const maxY = Math.max(1, previewCanvas.height - drag.box.h);
  const freeX = clamp01((point.x - drag.grabX) / maxX);
  const freeY = clamp01((point.y - drag.grabY) / maxY);

  setPositionMode("free");
  el<HTMLInputElement>("freeX").value = (freeX * 100).toFixed(1);
  el<HTMLInputElement>("freeY").value = (freeY * 100).toFixed(1);
  updatePosHint();

  const box = currentLayout();
  if (box) {
    drag.box = box;
    drawDragOverlay(box);
    previewInfo.textContent = `拖动中：X ${(freeX * 100).toFixed(1)}% · Y ${(freeY * 100).toFixed(1)}%`;
  }
});

function endDrag(event: PointerEvent): void {
  if (!drag.active) return;
  drag.active = false;
  previewCanvas.style.cursor = "move";
  try {
    if (previewCanvas.hasPointerCapture(event.pointerId)) previewCanvas.releasePointerCapture(event.pointerId);
  } catch {
    // 忽略：没有捕获时无需释放
  }
  saveState();
  schedulePreview(true); // 松手后用真实管线重绘一次
}

previewCanvas.addEventListener("pointerup", endDrag);
previewCanvas.addEventListener("pointercancel", endDrag);

el<HTMLButtonElement>("useAnchor").addEventListener("click", () => {
  setPositionMode("anchor");
  saveState();
  schedulePreview(true);
});

startBtn.addEventListener("click", () => void start());

/** 面板里任何改动都走这里：动自由坐标→free 模式，动九宫格参数→anchor 模式 */
function handlePanelChange(event: Event): void {
  const id = (event.target as HTMLElement | null)?.id;
  if (id === "freeX" || id === "freeY") setPositionMode("free");
  else if (id === "anchor" || id === "offX" || id === "offY") setPositionMode("anchor");
  syncOutputs();
  updatePosHint();
  saveState();
  schedulePreview();
}

panel.addEventListener("input", handlePanelChange);
panel.addEventListener("change", handlePanelChange);

// ---------- 初始化 ----------

/** 图片处理依赖 OffscreenCanvas + convertToBlob（Chrome 69+ / Firefox 105+ / Safari 16.4+） */
function isImagePipelineSupported(): boolean {
  return (
    typeof OffscreenCanvas !== "undefined" &&
    typeof OffscreenCanvas.prototype.convertToBlob === "function"
  );
}

if (!isFileSystemAccessSupported()) {
  const pickBtn = el<HTMLButtonElement>("pickDir");
  pickBtn.disabled = true;
  pickBtn.title = "当前浏览器不支持文件夹读写，请用 Chrome / Edge";
  log("提示：当前浏览器不支持「选择文件夹」，可用「选择图片文件」或「载入示例图」");
}

if (!isImagePipelineSupported()) {
  startBtn.disabled = true;
  startBtn.title = "当前浏览器不支持 OffscreenCanvas，无法处理图片";
  log("提示：当前浏览器缺少 OffscreenCanvas（图片处理必需），无法处理图片。请升级到 Chrome 69+ / Firefox 105+ / Safari 16.4+。");
}

loadState();
refreshCounters();
log("就绪。所有处理都在本地浏览器完成，不会上传任何文件。");
